import { useState, useCallback, useEffect } from "preact/hooks";
import type { PageContext } from "@pankagent/shared";
import type { DisplayMessage, WidgetInitConfig } from "../types/widget";
import { useChatSession } from "./useChatSession";
import { useChatStream } from "./useChatStream";
import { usePageTracking } from "./usePageTracking";
import { getPageContentAsMarkdown } from "../services/getPageContentAsMarkdown";

export type ViewMode = "condensed" | "expanded" | "full";

export interface UseChatReturn {
  // State
  viewMode: ViewMode;
  messages: DisplayMessage[];
  isLoading: boolean;
  sessionId: string | null;
  isInitialized: boolean;

  // Actions
  open: () => void;
  close: () => void;
  toggle: () => void;
  setViewMode: (mode: ViewMode) => void;
  sendMessage: (content: string) => Promise<void>;
}

/**
 * Main hook that orchestrates all chat functionality
 */
export function useChat(config: WidgetInitConfig): UseChatReturn {
  const isEmbedded = config.mode === "embedded";
  // Embedded mode starts in "full", overlay starts in "condensed"
  const [viewMode, setViewMode] = useState<ViewMode>(
    isEmbedded ? "full" : "condensed"
  );
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const hasStartedConversation = messages.some((m) => m.role === "user");

  // Sub-hooks
  const {
    sessionId,
    isInitialized,
    isInitializing,
    initSession,
    setSessionId,
  } = useChatSession({
    apiUrl: config.apiUrl,
    tenantId: config.tenantId,
    visitorId: config.visitorId,
    welcomeMessage: config.welcomeMessage,
    mode: config.mode,
  });

  const { stream } = useChatStream({
    apiUrl: config.apiUrl,
    tenantId: config.tenantId,
  });

  const { getRecentPages } = usePageTracking();

  // Get current page context
  const getPageContext = useCallback(
    (): PageContext => ({
      url: window.location.href,
      title: document.title,
      referrer: document.referrer,
      customData: config.customData,
    }),
    [config.customData]
  );

  // Initialize session when widget opens (or immediately for embedded mode)
  useEffect(() => {
    const shouldInit = isEmbedded || viewMode !== "condensed";
    if (shouldInit && !isInitialized && !isInitializing) {
      setIsLoading(true);
      initSession(getPageContext()).then((welcomeMessage) => {
        if (welcomeMessage) {
          setMessages([welcomeMessage]);
        }
        setIsLoading(false);
      });
    }
  }, [
    viewMode,
    isEmbedded,
    isInitialized,
    isInitializing,
    initSession,
    getPageContext,
  ]);

  // Actions
  const open = useCallback(() => {
    if (isEmbedded) return; // Embedded mode is always open
    setViewMode(hasStartedConversation ? "full" : "expanded");
  }, [hasStartedConversation, isEmbedded]);

  const close = useCallback(() => {
    if (isEmbedded) return; // Embedded mode cannot be closed
    setViewMode("condensed");
  }, [isEmbedded]);

  const toggle = useCallback(() => {
    setViewMode((prev) => {
      if (prev !== "condensed") return "condensed";
      return hasStartedConversation ? "full" : "expanded";
    });
  }, [hasStartedConversation]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      // Extract page content for AI context enrichment
      const pageContent = getPageContentAsMarkdown();
      console.log("📄 Page content extracted for AI context:", {
        markdownLength: pageContent.stats.markdownLength,
        extractionTimeMs: pageContent.stats.extractionTimeMs,
        truncated: pageContent.truncated,
        metadata: pageContent.metadata,
        markdown: pageContent.markdown,
      });

      setViewMode("full");

      const userMessage: DisplayMessage = {
        id: `user_${Date.now()}`,
        role: "user",
        content: content.trim(),
      };

      // Add user message
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      // Add placeholder for assistant response
      const placeholderId = `assistant_${Date.now()}`;
      const assistantPlaceholder: DisplayMessage = {
        id: placeholderId,
        role: "assistant",
        content: "",
        isStreaming: true,
      };
      setMessages((prev) => [...prev, assistantPlaceholder]);

      let currentMessageId = placeholderId;
      let streamedContent = "";

      await stream(
        {
          sessionId,
          message: content.trim(),
          pageContext: getPageContext(),
          recentPages: getRecentPages(3),
        },
        {
          onSessionInfo: (newSessionId, messageId) => {
            setSessionId(newSessionId);
            currentMessageId = messageId;
          },
          onTextChunk: (chunk) => {
            streamedContent += chunk;
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === placeholderId || msg.id === currentMessageId
                  ? {
                      ...msg,
                      id: currentMessageId,
                      content: streamedContent,
                      isStreaming: true,
                    }
                  : msg
              )
            );
          },
          onDone: () => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === currentMessageId
                  ? { ...msg, isStreaming: false }
                  : msg
              )
            );
            setIsLoading(false);
          },
          onError: (error) => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.isStreaming
                  ? {
                      ...msg,
                      content: error || "Une erreur est survenue.",
                      isStreaming: false,
                    }
                  : msg
              )
            );
            setIsLoading(false);
          },
        }
      );
    },
    [isLoading, sessionId, stream, getPageContext, getRecentPages, setSessionId]
  );

  return {
    viewMode,
    messages,
    isLoading: isLoading || isInitializing,
    sessionId,
    isInitialized,
    open,
    close,
    toggle,
    setViewMode,
    sendMessage,
  };
}
